// Zoom da timeline — porta de fitZoom/applyZoom/setZoom do edvid (app.js ~927–972).
// Escala logarítmica entre "cabe tudo" (minPps) e MAX_PPS; o slider ancora na
// agulha, a pinça do trackpad (wheel + ctrlKey) ancora no ponteiro.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { LABEL_W, MAX_PPS } from './edvidTime';

export function useTimelineZoom(panelRef: RefObject<HTMLDivElement>, total: number, getNeedleT: () => number) {
  const [pps, setPps] = useState(10);
  const [minPps, setMinPps] = useState(4);
  const pendingScroll = useRef<number | null>(null);
  const fittedRef = useRef(true);
  const stateRef = useRef({ pps, minPps, total });
  stateRef.current = { pps, minPps, total };
  const needleRef = useRef(getNeedleT);
  needleRef.current = getNeedleT;

  const computeMin = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return 4;
    const avail = panel.clientWidth - LABEL_W - 40;
    return Math.max(1, avail / Math.max(stateRef.current.total, 1));
  }, [panelRef]);

  const fit = useCallback(() => {
    const m = computeMin();
    fittedRef.current = true;
    setMinPps(m);
    setPps(m);
    pendingScroll.current = 0;
  }, [computeMin]);

  // Zoom mantendo `t` (s) estacionado em `anchorX` (px da borda esquerda do painel).
  const applyZoom = useCallback((next: number, t: number, anchorX: number) => {
    const { minPps: lo } = stateRef.current;
    const p = Math.min(MAX_PPS, Math.max(lo, next));
    fittedRef.current = p <= lo + 1e-6;
    pendingScroll.current = Math.max(0, LABEL_W + t * p - anchorX);
    setPps(p);
  }, []);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (panel && pendingScroll.current != null) {
      panel.scrollLeft = pendingScroll.current;
      pendingScroll.current = null;
    }
  }, [pps, panelRef]);

  // Refit quando a duração ou a largura mudam — mas só se o usuário ainda estiver
  // no "cabe tudo"; um zoom escolhido não é desfeito por um resize.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const refresh = () => {
      const m = computeMin();
      setMinPps(m);
      if (fittedRef.current) setPps(m);
      else setPps(p => Math.max(p, m));
    };
    refresh();
    const ro = new ResizeObserver(refresh);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [panelRef, total, computeMin]);

  // Pinça do trackpad chega como wheel com ctrlKey. Rolagem com dois dedos fica intocada.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const pr = panel.getBoundingClientRect();
      const anchorX = e.clientX - pr.left;
      const cur = stateRef.current.pps;
      const t = (panel.scrollLeft + anchorX - LABEL_W) / cur;
      applyZoom(cur * Math.exp(-e.deltaY * 0.01), Math.max(0, t), anchorX);
    };
    panel.addEventListener('wheel', onWheel, { passive: false });
    return () => panel.removeEventListener('wheel', onWheel);
  }, [panelRef, applyZoom]);

  const span = Math.log(MAX_PPS / Math.max(minPps, 1e-6));
  const slider = span > 0 ? Math.round((100 * Math.log(pps / minPps)) / span) : 0;

  /** slider 0..100 → minPps..MAX_PPS, ancorado na agulha (ou no meio, se ela estiver fora da tela) */
  const setSlider = useCallback((v: number) => {
    const panel = panelRef.current;
    if (!panel) return;
    const { pps: cur, minPps: lo } = stateRef.current;
    const t = needleRef.current();
    const xBefore = LABEL_W + t * cur - panel.scrollLeft;
    const visible = xBefore >= LABEL_W && xBefore <= panel.clientWidth;
    const anchor = visible ? xBefore : LABEL_W + (panel.clientWidth - LABEL_W) / 2;
    applyZoom(lo * Math.pow(MAX_PPS / lo, v / 100), t, anchor);
  }, [panelRef, applyZoom]);

  const zoomBy = useCallback((factor: number) => {
    const panel = panelRef.current;
    if (!panel) return;
    const { pps: cur } = stateRef.current;
    const t = needleRef.current();
    const x = LABEL_W + t * cur - panel.scrollLeft;
    const anchor = x >= LABEL_W && x <= panel.clientWidth ? x : panel.clientWidth / 2;
    applyZoom(cur * factor, t, anchor);
  }, [panelRef, applyZoom]);

  return { pps, minPps, slider, setSlider, fit, zoomBy };
}
